"use client";

import { useEffect, useState } from "react";
import { Menu, Crosshair } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
function DesktopMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex gap-2">
        <div className="h-4 w-16 bg-muted animate-pulse" />
        <div className="h-4 w-16 bg-muted animate-pulse" />
        <div className="h-4 w-16 bg-muted animate-pulse" />
      </div>;
  }
  return <NavigationMenu viewport={false}>
      <NavigationMenuList className="gap-6">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="relative text-foreground hover:bg-transparent hover:text-primary focus:bg-transparent focus:text-primary text-xs font-default font-bold uppercase tracking-[0.25em] px-0 py-2 bg-transparent group">
                <span>{item.label}</span>
                <span className="absolute -bottom-1 left-0 h-px w-0 bg-primary transition-all duration-300 group-hover:w-full" />
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer({
  onItemClick
}: {
  onItemClick: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-6">
        <div className="h-12 w-3/4 bg-muted animate-pulse" />
        <div className="h-12 w-2/3 bg-muted animate-pulse" />
        <div className="h-12 w-3/5 bg-muted animate-pulse" />
      </div>;
  }
  return <nav className="flex flex-col gap-8">
      {menuItems.map((item, i) => <Link key={item.id} to={item.href || "#"} onClick={onItemClick} data-index={i} className="text-foreground hover:text-primary text-4xl sm:text-5xl font-default font-bold uppercase tracking-tight leading-none transition-colors duration-200">
          {item.label}
        </Link>)}
    </nav>;
}
export default function MainNavigation() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    handleScroll();
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <section data-section-id="4435" id="main-navigation" className={`sticky top-0 left-0 right-0 z-50 w-full bg-background text-foreground transition-all duration-300 ${scrolled ? "bg-background/70 backdrop-blur-xl shadow-lg border-b border-border" : "bg-background border-b border-border"}`}>
      <div className="w-full px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between gap-6">
        {}
        <div className="flex flex-col justify-center">
          
          <Link to="/" className="flex items-center gap-1.5 group">
            <span className="text-foreground text-2xl font-default font-bold uppercase tracking-tight leading-none">
              VIVID
            </span>
            <Crosshair className="h-6 w-6 text-primary transition-transform duration-300 group-hover:rotate-90" strokeWidth={2.5} />
            <span className="text-foreground text-2xl font-default font-bold uppercase tracking-tight leading-none">
              STUDIO
            </span>
          </Link>
        </div>

        {}
        <div className="hidden lg:flex items-center flex-1 justify-end gap-10">
          <MenuProvider menu_id="2" className="header-menu">
            <DesktopMenuConsumer />
          </MenuProvider>

          <Button asChild className="bg-primary text-primary-foreground hover:bg-primary/90 text-xs font-default font-bold uppercase tracking-[0.25em] h-11 px-6 shadow-md transition-shadow">
            <Link to="/StartProjectPage">Start Project</Link>
          </Button>
        </div>

        {}
        <div className="lg:hidden flex items-center">
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="h-11 w-11 text-foreground hover:bg-accent hover:text-primary" aria-label="Open menu">
                <Menu className="h-6 w-6" strokeWidth={1.5} />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-full sm:max-w-full bg-background text-foreground border-l border-border p-0 flex flex-col">
              <SheetTitle className="sr-only">Navigation Menu</SheetTitle>
              <div className="flex flex-col h-full px-8 pt-20 pb-10">
                <div className="flex-1 flex flex-col justify-center">
                  <MenuProvider menu_id="2" className="header-menu-mobile">
                    <MobileMenuConsumer onItemClick={() => setMobileOpen(false)} />
                  </MenuProvider>

                  <div className="mt-12">
                    <Button asChild onClick={() => setMobileOpen(false)} className="bg-primary text-primary-foreground hover:bg-primary/90 text-xs font-default font-bold uppercase tracking-[0.25em] h-12 px-8 shadow-md">
                      <Link to="/StartProjectPage">Start Project</Link>
                    </Button>
                  </div>
                </div>

                <div className="pt-8 border-t border-border">
                  <span className="text-[10px] font-mono text-muted-foreground tracking-widest uppercase">
                    VIVID // STUDIO
                  </span>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </section>;
}